// How a plugin's code is brought to life. One factory interface, two trust
// tiers (see decisions D2):
//   - inProcessRuntimeFactory: trusted tier. Runs a first-party plugin MODULE
//     directly in the host context over an in-memory channel. Also used by all
//     tests. No sandbox cost. NEVER use for untrusted third-party code.
//   - sealedWorkerRuntimeFactory (Task 6): untrusted tier. Origin-isolated
//     Worker under a strict CSP. Used for third-party plugins.

import {
  startPlugin,
  type RpcChannel,
  type RpcMessage,
  type PluginModule,
  type PluginManifest,
  type SignatureManifest,
} from '@slate/plugin-api';

export interface InstalledPlugin {
  manifest: PluginManifest;
  /** First-party / in-process (trusted) tier supplies the module directly. */
  module?: PluginModule;
  /** Third-party / sealed-worker tier supplies the asset base URL instead. */
  baseUrl?: string;
  /** Sealed tier: the parsed sidecar signature.json (for load-time verify). */
  signature?: SignatureManifest;
}

export interface PluginRuntimeHandle {
  channel: RpcChannel;
  /** Tear the running plugin down (terminate the worker / dispose in-process). */
  dispose(): void | Promise<void>;
}

export type PluginRuntimeFactory = (
  plugin: InstalledPlugin,
) => PluginRuntimeHandle | Promise<PluginRuntimeHandle>;

/** Two channels wired together: posting on one delivers to the other. */
export function inMemoryChannelPair(): [RpcChannel, RpcChannel] {
  const aListeners = new Set<(m: RpcMessage) => void>();
  const bListeners = new Set<(m: RpcMessage) => void>();
  return [
    {
      post: (m) => bListeners.forEach((l) => l(m)),
      onMessage: (cb) => {
        aListeners.add(cb);
        return () => aListeners.delete(cb);
      },
    },
    {
      post: (m) => aListeners.forEach((l) => l(m)),
      onMessage: (cb) => {
        bListeners.add(cb);
        return () => bListeners.delete(cb);
      },
    },
  ];
}

/** Trusted tier: run a first-party plugin module in-process. */
export function inProcessRuntimeFactory(): PluginRuntimeFactory {
  return (plugin) => {
    if (!plugin.module) {
      throw new Error(`in-process plugin "${plugin.manifest.id}" has no module`);
    }
    const [hostCh, workerCh] = inMemoryChannelPair();
    const rt = startPlugin(workerCh, plugin.module);
    return {
      channel: hostCh,
      dispose: () => rt.dispose(),
    };
  };
}

/** Wrap a real Web Worker as an RpcChannel (host side of the sealed tier). */
export function workerChannel(worker: Worker): RpcChannel {
  return {
    post: (msg) => worker.postMessage(msg),
    onMessage: (cb) => {
      const handler = (e: MessageEvent): void => cb(e.data as RpcMessage);
      worker.addEventListener('message', handler);
      return () => worker.removeEventListener('message', handler);
    },
  };
}

export interface SealedWorkerOptions {
  /** Create the origin-isolated module Worker from the plugin's worker URL.
   *  Default uses the platform Worker; tests inject a fake. */
  createWorker?: (url: string) => Worker;
}

/**
 * Untrusted tier: load the plugin's bundled worker.js from its locked-down
 * origin (a `plugin:` URL served with a strict CSP) as a module Worker. The
 * worker.js calls `startPlugin(selfChannel(), main)`; the host drives the
 * lifecycle over the channel. The CSP/origin enforcement is verified by manual
 * QA in the desktop webview (see decisions D1).
 */
export function sealedWorkerRuntimeFactory(opts: SealedWorkerOptions = {}): PluginRuntimeFactory {
  const create = opts.createWorker ?? ((url: string) => new Worker(url, { type: 'module' }));
  return (plugin) => {
    if (!plugin.baseUrl) {
      throw new Error(`sealed plugin "${plugin.manifest.id}" has no baseUrl`);
    }
    const main = plugin.manifest.main ?? 'worker.js';
    const worker = create(`${plugin.baseUrl.replace(/\/$/, '')}/${main}`);
    return {
      channel: workerChannel(worker),
      dispose: () => worker.terminate(),
    };
  };
}
